import { create } from "zustand";
import type { ChatEntry, ConfirmationAction } from "../ctui/chat/types";

export type ChatMode = "cinematic" | "listening" | "thinking";

interface ChatState {
  mode: ChatMode;
  entries: ChatEntry[];
  append: (entry: ChatEntry) => void;
  appendToken: (id: string, token: string) => void;
  setEntryText: (id: string, text: string) => void;
  setStreaming: (id: string, streaming: boolean) => void;
  resolveConfirmation: (id: string, action: ConfirmationAction) => void;
  completeAction: (id: string) => void;
  overrideAction: (id: string) => void;
  /** Drop a small ripper "drill-in" hint — only fires when mode is listening. */
  pushDrillHint: (text: string) => void;
  setMode: (mode: ChatMode) => void;
  reset: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  mode: "cinematic",
  entries: [],
  append: (entry) =>
    set((state) => ({ entries: [...state.entries, entry] })),
  appendToken: (id, token) =>
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id && e.kind === "ripper"
          ? { ...e, text: e.text + token }
          : e
      ),
    })),
  setEntryText: (id, text) =>
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id && e.kind === "ripper" ? { ...e, text } : e
      ),
    })),
  setStreaming: (id, streaming) =>
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id && e.kind === "ripper" ? { ...e, streaming } : e
      ),
    })),
  resolveConfirmation: (id, action) =>
    set((state) => {
      const next = state.entries.map((e) =>
        e.id === id && e.kind === "confirmation" ? { ...e, resolvedWith: action } : e
      );
      if (action.receipt) {
        next.push({
          id: `receipt-${id}-${action.key}`,
          kind: "receipt",
          text: action.receipt,
        });
      }
      return { entries: next };
    }),
  completeAction: (id) =>
    set((state) => {
      const target = state.entries.find((e) => e.id === id);
      if (!target || target.kind !== "action" || target.state !== "pending") {
        return {};
      }
      const next: ChatEntry[] = state.entries.map((e) =>
        e.id === id && e.kind === "action" ? { ...e, state: "done" } : e
      );
      next.push({ id: `receipt-${id}`, kind: "receipt", text: target.receipt });
      return { entries: next };
    }),
  overrideAction: (id) =>
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id && e.kind === "action" && e.state === "pending"
          ? { ...e, state: "overridden" }
          : e
      ),
    })),
  pushDrillHint: (text) =>
    set((state) => {
      if (state.mode !== "listening") return {};
      return {
        entries: [
          ...state.entries,
          { id: `hint-${Date.now()}`, kind: "ripper", text, streaming: false },
        ],
      };
    }),
  setMode: (mode) => set({ mode }),
  reset: () => set({ entries: [], mode: "cinematic" }),
}));
